export type StripeEnvironmentName = "sandbox" | "live";

/**
 * Pricing is stored PER Stripe environment: editing sandbox prices must never
 * change what live customers are charged. `pricing_display` is kept only as a
 * read-only fallback for rows written before the split.
 */
export const LEGACY_PRICING_SETTINGS_KEY = "pricing_display";

export function pricingSettingsKey(environment: StripeEnvironmentName): string {
  return environment === "live" ? "pricing_display_live" : "pricing_display_sandbox";
}

export const PRICING_SETTINGS_KEYS = [
  LEGACY_PRICING_SETTINGS_KEY,
  pricingSettingsKey("sandbox"),
  pricingSettingsKey("live"),
];

/** Value for one environment, falling back to the legacy shared key. */
export function pickPricingValue(
  settings: Record<string, unknown>,
  environment: StripeEnvironmentName,
): unknown {
  return settings[pricingSettingsKey(environment)] ?? settings[LEGACY_PRICING_SETTINGS_KEY];
}

export type PricingEntry = {
  amount: number;
  currency: string;
  label: string;
  interval?: string;
};

export type PricingMap = Record<string, PricingEntry>;

export type UsageLimits = {
  anon: { monthly: number };
  free: { monthly: number };
  premium: { daily: number; monthly: number };
  pro: { daily: number; monthly: number };
};

export const DEFAULT_PRICING: PricingMap = {
  pro_monthly: { amount: 999, currency: "eur", label: "€9,99", interval: "month" },
  premium_monthly: { amount: 399, currency: "eur", label: "€3,99", interval: "month" },
  premium_yearly: { amount: 2999, currency: "eur", label: "€29,99", interval: "year" },
  credits_pack_10: { amount: 499, currency: "eur", label: "€4,99" },
  credits_pack_30: { amount: 1199, currency: "eur", label: "€11,99" },
  credits_pack_100: { amount: 2999, currency: "eur", label: "€29,99" },
};

export const DEFAULT_USAGE_LIMITS: UsageLimits = {
  anon: { monthly: 2 },
  free: { monthly: 5 },
  premium: { daily: 10, monthly: 15 },
  pro: { daily: 5, monthly: 50 },
};

export function formatPricingAmount(amount: number, currency: string): string {
  const safeAmount = Number.isFinite(amount) ? Math.max(0, Math.round(amount)) : 0;
  const value = (safeAmount / 100).toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (currency.toLowerCase() === "eur") return `€${value}`;
  return `${value} ${currency.toUpperCase()}`;
}

export function formatPricingEntry(entry: Pick<PricingEntry, "amount" | "currency">): string {
  return formatPricingAmount(entry.amount, entry.currency);
}

export function normalizePricingMap(value: unknown): PricingMap {
  const source = value && typeof value === "object" ? (value as Record<string, Partial<PricingEntry>>) : {};
  const keys = new Set([...Object.keys(DEFAULT_PRICING), ...Object.keys(source)]);
  const result: PricingMap = {};
  for (const key of keys) {
    const fallback = DEFAULT_PRICING[key] ?? { amount: 0, currency: "eur", label: "€0,00" };
    const candidate = source[key] ?? {};
    const amount = Number.isInteger(candidate.amount) && Number(candidate.amount) >= 0
      ? Number(candidate.amount)
      : fallback.amount;
    const currency = typeof candidate.currency === "string" && /^[a-z]{3,4}$/i.test(candidate.currency)
      ? candidate.currency.toLowerCase()
      : fallback.currency;
    const interval = typeof candidate.interval === "string" ? candidate.interval : fallback.interval;
    result[key] = {
      amount,
      currency,
      label: formatPricingAmount(amount, currency),
      ...(interval ? { interval } : {}),
    };
  }
  return result;
}

function safeLimit(value: unknown, fallback: number): number {
  return Number.isInteger(value) && Number(value) >= 0 ? Number(value) : fallback;
}

export function normalizeUsageLimits(value: unknown): UsageLimits {
  const source = value && typeof value === "object" ? value as Partial<UsageLimits> : {};
  return {
    anon: { monthly: safeLimit(source.anon?.monthly, DEFAULT_USAGE_LIMITS.anon.monthly) },
    free: { monthly: safeLimit(source.free?.monthly, DEFAULT_USAGE_LIMITS.free.monthly) },
    premium: {
      daily: safeLimit(source.premium?.daily, DEFAULT_USAGE_LIMITS.premium.daily),
      monthly: safeLimit(source.premium?.monthly, DEFAULT_USAGE_LIMITS.premium.monthly),
    },
    pro: {
      daily: safeLimit(source.pro?.daily, DEFAULT_USAGE_LIMITS.pro.daily),
      monthly: safeLimit(source.pro?.monthly, DEFAULT_USAGE_LIMITS.pro.monthly),
    },
  };
}

export function annualSavingsPercent(monthlyCents: number, yearlyCents: number): number {
  if (monthlyCents <= 0 || yearlyCents < 0) return 0;
  return Math.max(0, Math.min(99, Math.round((1 - yearlyCents / (monthlyCents * 12)) * 100)));
}
